import { Component } from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

/**
 * Ловит ошибки рендера поддерева.
 *
 * Без него любое исключение в любом компоненте размонтирует всё дерево и
 * оставляет человека наедине с белым экраном — без объяснения, без кнопки и
 * без единого следа в интерфейсе. Здесь же он видит, что произошло, может
 * перезагрузить страницу и назвать поддержке идентификатор ошибки.
 *
 * Классовый компонент, потому что componentDidCatch у хуков аналога не имеет.
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null, incidentId: null };
    }

    static getDerivedStateFromError(error) {
        return {
            error,
            // Короткий код, который человек может продиктовать в поддержку.
            incidentId: Math.random().toString(36).slice(2, 10).toUpperCase(),
        };
    }

    componentDidCatch(error, errorInfo) {
        // Консоль — единственный доступный здесь канал: сюда смотрит
        // разработчик, а стек в state класть незачем, он не для показа.
        console.error("Ошибка рендера:", error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleHome = () => {
        window.location.href = "/";
    };

    render() {
        if (!this.state.error) return this.props.children;

        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    background: "var(--background, #f4f5f7)",
                    color: "var(--foreground, #111827)",
                }}
            >
                <div
                    style={{
                        maxWidth: 480,
                        width: "100%",
                        background: "var(--card-bg, #ffffff)",
                        border: "1px solid var(--card-border, #e5e7eb)",
                        borderRadius: 20,
                        padding: 32,
                        backdropFilter: "blur(20px)",
                        textAlign: "center",
                    }}
                >
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            margin: "0 auto 20px",
                            borderRadius: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(249, 115, 22, 0.12)",
                            color: "#f97316",
                        }}
                    >
                        <AlertTriangle size={28} />
                    </div>

                    <h1 style={{ margin: "0 0 10px", fontSize: 20, fontWeight: 600 }}>
                        Что-то сломалось
                    </h1>
                    <p
                        style={{
                            margin: "0 0 8px",
                            fontSize: 14,
                            lineHeight: 1.6,
                            color: "var(--muted-foreground, #6b7280)",
                        }}
                    >
                        Страница не смогла отрисоваться. Данные при этом никуда не делись —
                        попробуйте перезагрузить.
                    </p>
                    <p
                        style={{
                            margin: "0 0 24px",
                            fontSize: 12,
                            color: "var(--muted-foreground, #9ca3af)",
                        }}
                    >
                        Код ошибки: <b>{this.state.incidentId}</b> — назовите его в поддержке
                    </p>

                    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                        <button
                            onClick={this.handleReload}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "10px 18px",
                                borderRadius: 12,
                                border: "none",
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: 500,
                                background: "#f97316",
                                color: "#ffffff",
                            }}
                        >
                            <RotateCcw size={16} /> Перезагрузить
                        </button>
                        <button
                            onClick={this.handleHome}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "10px 18px",
                                borderRadius: 12,
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: 500,
                                background: "transparent",
                                border: "1px solid var(--card-border, #e5e7eb)",
                                color: "var(--foreground, #111827)",
                            }}
                        >
                            <Home size={16} /> На главную
                        </button>
                    </div>

                    {process.env.NODE_ENV === "development" && (
                        <pre
                            style={{
                                marginTop: 24,
                                padding: 12,
                                borderRadius: 10,
                                background: "rgba(239, 68, 68, 0.08)",
                                color: "#ef4444",
                                fontSize: 11,
                                textAlign: "left",
                                overflow: "auto",
                                maxHeight: 200,
                            }}
                        >
                            {String(this.state.error?.stack || this.state.error)}
                        </pre>
                    )}
                </div>
            </div>
        );
    }
}
